import { easeInOut, motion } from 'motion/react'
import { ArrowRight, Calendar } from 'lucide-react'

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

export const HeroSection = () => {
  return (
    <div className="relative overflow-hidden bg-[#001871] text-white">
      {/* Enhanced texture overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
      <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

      {/* Abstract shapes with improved contrast */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.15, scale: 1 }}
        transition={{ duration: 2 }}
        className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.15, scale: 1 }}
        transition={{ duration: 2, delay: 0.5 }}
        className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
      />

      {/* Floating elements with improved visibility */}
      <motion.div
        variants={floatingAnimation}
        initial="initial"
        animate="animate"
        className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
      />
      <motion.div
        variants={floatingAnimation}
        initial="initial"
        animate="animate"
        transition={{ delay: 1 }}
        className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
      />

      <div className="relative pb-40 pt-32 sm:pb-48 sm:pt-40">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="space-y-8"
            >
              <motion.div
                variants={glowAnimation}
                initial="initial"
                animate="animate"
                className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
              >
                Break Free from Manual Literature Reviews
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
              >
                Your Team Deserves Better Than Endless Excel Sheets
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
              >
                Transform your literature review process from weeks to days. Our smart automation
                helps medical device teams ensure compliance while saving valuable time.
              </motion.p>

              <div className="mt-10">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex justify-center"
                >
                  <a
                    href="https://calendly.com/hatem-rabeh/30-min-seesion-with-dr-hatem-rabeh"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.button className="group relative flex items-center gap-2 overflow-hidden rounded-xl bg-white px-8 py-4 text-lg font-bold text-[#001871] shadow-xl transition-all duration-300 hover:bg-white/90">
                      <Calendar className="size-5 text-[#001871] group-hover:animate-bounce" />
                      <span className="relative z-10">Transform Your Review Process Today</span>
                      <ArrowRight className="ml-2 size-5 text-[#001871] transition-transform duration-300 group-hover:translate-x-1" />
                    </motion.button>
                  </a>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Wave transition with improved contrast */}
      <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden leading-none">
        <svg
          className="relative block h-[50px] w-full"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="fill-white"
          ></path>
        </svg>
      </div>
    </div>
  )
}
